/**
 * Studio timezone state and pure zoned-date helpers (issue #203).
 *
 * Lesson times are stored and transported as UTC (`starts_at`). The
 * studio's IANA timezone — fetched from `GET /api/me` (issue #202) —
 * decides how those instants are *rendered* and how local calendar-day
 * boundaries are derived.
 *
 * The timezone is module-level state, set once the session hydrates. It
 * deliberately defaults to the backend's default so every screen has a
 * sane value before `/api/me` resolves. Tests that assert wall-clock
 * strings should set it explicitly.
 *
 * Everything here is implemented with `Intl.DateTimeFormat`, so no date
 * library is needed and DST transitions are handled by the platform.
 */

export const DEFAULT_STUDIO_TIMEZONE = "Europe/Rome";

let studioTimezone = DEFAULT_STUDIO_TIMEZONE;

/** The current studio IANA timezone. */
export function getStudioTimezone(): string {
  return studioTimezone;
}

/**
 * Sets the studio timezone. Empty / nullish values are ignored, so a
 * response without `studio_timezone` never clobbers the current value.
 */
export function setStudioTimezone(timezone: string | null | undefined): void {
  if (typeof timezone === "string" && timezone.length > 0) {
    studioTimezone = timezone;
  }
}

export interface ZonedParts {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export interface LocalDateParts {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, formatter);
  }

  return formatter;
}

/** The wall-clock parts of `date` in `timeZone`. */
export function zonedParts(
  date: Date,
  timeZone: string = studioTimezone,
): ZonedParts {
  const values: Record<string, string> = {};

  for (const part of partsFormatter(timeZone).formatToParts(date)) {
    values[part.type] = part.value;
  }

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    // Some engines render midnight as "24" even with `hour12: false`.
    hour: Number(values.hour) % 24,
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-03-03" — the local calendar day of `date` in `timeZone`. */
export function zonedDateKey(date: Date, timeZone: string = studioTimezone): string {
  const { year, month, day } = zonedParts(date, timeZone);
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** "2026-03" — the local calendar month of `date` in `timeZone`. */
export function zonedMonthKey(date: Date, timeZone: string = studioTimezone): string {
  const { year, month } = zonedParts(date, timeZone);
  return `${year}-${pad(month)}`;
}

/** Offset of `timeZone` from UTC at the instant `date`, in milliseconds (east positive). */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const { year, month, day, hour, minute, second } = zonedParts(date, timeZone);
  return Date.UTC(year, month - 1, day, hour, minute, second) - date.getTime();
}

/**
 * The UTC instant for a wall-clock time in `timeZone`.
 *
 * The offset is applied twice so a DST transition between the initial
 * guess and the resolved instant is accounted for.
 */
export function zonedWallTimeToUtc(
  parts: LocalDateParts &
    Partial<Pick<ZonedParts, "hour" | "minute" | "second">>,
  timeZone: string = studioTimezone,
): Date {
  const { year, month, day, hour = 0, minute = 0, second = 0 } = parts;
  const guess = Date.UTC(year, month - 1, day, hour, minute, second);

  const firstOffset = zoneOffsetMs(new Date(guess), timeZone);
  let timestamp = guess - firstOffset;

  const secondOffset = zoneOffsetMs(new Date(timestamp), timeZone);
  if (secondOffset !== firstOffset) {
    timestamp = guess - secondOffset;
  }

  return new Date(timestamp);
}

/** "YYYY-MM-DD" arithmetic on a plain calendar date (no timezone). */
export function addLocalDays(date: LocalDateParts, days: number): LocalDateParts {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/** The start of the local day containing `date` (UTC instant). */
export function zonedStartOfDay(
  date: Date,
  timeZone: string = studioTimezone,
): Date {
  const { year, month, day } = zonedParts(date, timeZone);
  return zonedWallTimeToUtc({ year, month, day }, timeZone);
}

/** The end of the local day containing `date` (next local midnight − 1 ms). */
export function zonedEndOfDay(
  date: Date,
  timeZone: string = studioTimezone,
): Date {
  const { year, month, day } = zonedParts(date, timeZone);
  const next = addLocalDays({ year, month, day }, 1);
  return new Date(zonedWallTimeToUtc(next, timeZone).getTime() - 1);
}

/** ISO-8601 UTC ("…Z") → the `YYYY-MM-DDTHH:MM` a `datetime-local` input wants. */
export function toZonedDatetimeLocal(
  iso: string,
  timeZone: string = studioTimezone,
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const { year, month, day, hour, minute } = zonedParts(date, timeZone);
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

/**
 * A `datetime-local` value (`YYYY-MM-DDTHH:MM[:SS]`), interpreted as studio
 * wall-clock time, to ISO-8601 UTC with seconds and a `Z` suffix.
 */
export function fromZonedDatetimeLocal(
  value: string,
  timeZone: string = studioTimezone,
): string {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);

  if (!match) return "";

  const [, year, month, day, hour, minute, second = "0"] = match;
  const utc = zonedWallTimeToUtc(
    {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
      second: Number(second),
    },
    timeZone,
  );

  return utc.toISOString().replace(/\.\d{3}Z$/, "Z");
}
